import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import {
  getCmiDigestWatchlistCommunityRuns,
  getCmiDigestWatchlistRunCommunityDetail,
} from 'apis/CmiDigestWatchlistAPI';
import DigestSelector from 'components/CommunitiesToWatch/DigestSelector';
import DetailsButton from 'components/lib/DetailsButton';
import { useAppSelector } from 'store/hook';

import AiSummaryColumn from './AiSummaryColumn';
import DigestSectionCard from './DigestSectionCard';
import { Columns, SelectorRow } from './DigestSectionLayout';
import DigestSectionState from './DigestSectionState';
import NotableItemsColumn from './NotableItemsColumn';

type Props = {
  communityId?: number;
};

const WeeklyDigestCard = ({ communityId }: Props) => {
  const navigate = useNavigate();
  const { currentUser } = useAppSelector((state) => state.auth);
  const digestEnabled = Boolean(currentUser?.cmi_digest_preference?.enabled);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);

  const communityRuns = useQuery({
    queryKey: ['cmi-digest-watchlist-community-runs', communityId],
    queryFn: () => getCmiDigestWatchlistCommunityRuns(communityId ?? -1),
    enabled: digestEnabled && Boolean(communityId),
    refetchOnWindowFocus: false,
  });
  const selectedRun = digestEnabled
    ? (communityRuns.data?.find((run) => run.run_id === selectedRunId) ?? communityRuns.data?.[0] ?? null)
    : null;

  const detail = useQuery({
    queryKey: ['cmi-digest-watchlist-community-detail', selectedRun?.run_id, selectedRun?.run_community_id],
    queryFn: () =>
      getCmiDigestWatchlistRunCommunityDetail(selectedRun?.run_id ?? -1, selectedRun?.run_community_id ?? -1),
    enabled: Boolean(selectedRun),
    refetchOnWindowFocus: false,
  });

  const isLoading = communityRuns.isLoading || detail.isLoading;
  const isError = communityRuns.isError || detail.isError;
  const selected = detail.data;
  const runs = (communityRuns.data ?? []).map((run) => ({ ...run, id: run.run_id }));
  const action = selectedRun && (
    <DetailsButton
      data-test-id="weekly-digest-card-details"
      onClick={() =>
        navigate(`/communities-to-watch/${selectedRun.run_id}/communities/${selectedRun.run_community_id}`)
      }
    />
  );

  return (
    <DigestSectionCard
      title="Weekly Digest"
      subtitle="Notable weekly happenings for this community."
      action={action}
      testId="weekly-digest-card"
    >
      <DigestSectionState
        isLoading={isLoading}
        isError={isError}
        digestEnabled={digestEnabled}
        hasDigest={Boolean(selected)}
        emptyMessage="No delivered digest has included this community yet."
      />
      {!isLoading && !isError && selectedRun && selected && (
        <>
          <SelectorRow>
            <DigestSelector runs={runs} selectedRunId={selectedRun.run_id} onSelect={setSelectedRunId} />
          </SelectorRow>
          <Columns>
            <AiSummaryColumn lines={selected.summary} wide />
            <NotableItemsColumn items={selected.supporting_context} />
          </Columns>
        </>
      )}
    </DigestSectionCard>
  );
};

export default WeeklyDigestCard;
